import { Document } from '@contentful/rich-text-types'
import { RichTextPanelSupportingData, RichTextPanelProps } from '~/components/cms/RichTextPanel/RichTextPanel.vue'
import { ActionFields } from '~/components/cms/Action/actionHelpers'
import type { RecommendationRotatorProps } from '~/components/cms/RecommendationRotator/RecommendationRotator.vue'
import type { EntryCollection, EntrySkeletonType } from 'contentful'

type ProductPromotion = {
  linkTitle: string
  promotionDisclaimerPageLink: ActionFields
  sku: string
  promotionMessage: Document
  supportingData: RichTextPanelSupportingData
}

type BrazeBannerPreference = {
  placementIds?: string[]
  supportingData?: {
    home?: {
      width?: { value?: 'default' | 'full-width' }
      marginBottom?: { value?: boolean }
    }
  }
}

type SitePreferences = {
  articleSignUp: any
  brillianceConfirmationPanel: Document
  brillianceBanner: any
  googleVerificationToken: string
  productPromotion: ProductPromotion
  addToBagRecommender?: { fields?: RecommendationRotatorProps }
  quickSignUp: any
  showPlpTryOnIcon: boolean
  signupCouponCode: string
  signUpPanel: Document
  memberBenefitsPanel: RichTextPanelProps
  wishlistEmptyPanel?: { fields: RichTextPanelProps & { personalizationTags?: Array<'member' | 'non-member'> } }[]
  wishlistBanner?: AlertBar[]
  searchBrazeBanner?: BrazeBannerPreference
  wishlistBrazeBanner?: BrazeBannerPreference
  plpBrazeBanner?: BrazeBannerPreference
  topCategories: any
  standardDispatchTime?: number
  resizeEngravingDelayTime?: number
  transitAndStoreProcessingTime?: number
}

type PDPPreferences = {
  productUspAttribute: any
  productDetailsAttribute: any
  deliveryCallout: any
  pdpPaymentMethods: any
  paymentMethodsCallout: any
  pcpLink: ActionFields
  returnsDetails: any
  findInStoreTooltip: any
  engravingPanel: any
  sizeGuide: any
  preReviewsRotators: any
  postReviewsRotators: any
  businessUSPMessage: any
  madeToOrderLeadTimeCallout: any
  dispatchAndProcessingLeadTimeCallout: any
  pairItWithRotator: any
  columnPanel: any
  brazeBanner?: BrazeBannerPreference
  financeOptionsSlideInTitle: string
  financeOptionsSlideInDescription: string
  repaymentCallout: string
  earringReturnsCallout: {
    fields: RichTextPanelProps
  }
  changeMindReturnSlideIn: {
    fields: RichTextPanelProps
  }
  clickAndCollectSlideInContent: {
    fields: RichTextPanelProps
  }
  reserveInStoreSlideInContent: {
    fields: RichTextPanelProps
  }
}

type CartPreferences = {
  informationAlertBar: AlertBar[]
  orderInformationAccordion: any
  clickAndCollectDetails: Document
  productPromotion: ProductPromotion
  salesTaxTooltip: RichTextPanelProps
  emptyMyBagRecommender: any
  populatedMyBagRecommender: any
  orderInformationAccordionPanel: any
  brazeContent: any
  brazeBanner?: BrazeBannerPreference
  deliveryAssuranceItems: any
}

type PreferenceContentType = 'sitePreferences' | 'pdpPreferences' | 'cartPreferences'
type PreferenceContentTypes = PreferenceContentType[]

export const usePreferencesStore = defineStore('preferences', () => {
  const site = ref<SitePreferences>()
  const pdp = ref<PDPPreferences>()
  const cart = ref<CartPreferences>()

  // specify keys be fetched as arg eg: init(['sitePreferences', 'pdpPreferences']) or no args to fetch all eg: init()
  async function init(types: PreferenceContentTypes = ['sitePreferences', 'pdpPreferences', 'cartPreferences']) {
    try {
      const { isPreviewEnabled } = useContentfulPreview()
      const previewStore = useContentfulPreviewStore()

      const locale = useNuxtApp().$i18n.locale.value
      const { data } = await useAsyncData('sitePreference', handler, {
        deep: false,
        transform: transformer,
      })

      async function handler() {
        const { getEntries } = useContentful()
        const localeCode = localeConfig[locale].code
        const results = await Promise.all(
          types.map((contentType) =>
            getEntries({
              content_type: contentType,
              'fields.locale': localeCode,
            }),
          ),
        )

        return results.flatMap((entries) => entries?.items ?? [])
      }

      function topCategoriesTransformer(data: any) {
        if (!data) return
        const fields = data?.fields
        const supportingData = fields?.supportingData?.['home']
        const variant = supportingData?.variant?.value
        const width = supportingData?.width?.value
        const { cards, references } = fields
        return {
          variant,
          width,
          cards,
          references,
        }
      }

      function alertTransformer(alertBar: AlertBar[]) {
        if (!alertBar) return undefined
        const transformedAlertBar = alertBar.map((alert) => {
          const { fields } = alert
          const variant = fields?.supportingData?.['home']?.variant?.value
          let newVariant
          if (variant === 'criticalAlerts') {
            newVariant = 'error'
          } else if (variant === 'warningAlerts') {
            newVariant = 'warning'
          } else if (variant === 'informationAlerts') {
            newVariant = 'info'
          } else {
            newVariant = 'default'
          }
          return {
            fields: {
              ...fields,
              variant: newVariant,
            },
          }
        })
        return transformedAlertBar
      }

      function transformer(data: any) {
        const preferenceFields = (contentType: PreferenceContentType) =>
          data.findLast((item: any) => item.sys.contentType.sys.id === contentType)?.fields

        const {
          articleSignUp,
          brillianceBanner,
          brillianceConfirmationPanel,
          productPromotion,
          quickSignUp,
          signupCouponCode,
          signUpPanel,
          memberBenefitsPanel,
          loggedOutWishlistContent,
          loggedOutWishlistBanner,
          loggedInWishlistContent,
          loggedInWishlistBanner,
          wishlistEmptyPanel,
          wishlistBanner,
          searchBrazeBanner,
          wishlistBrazeBanner,
          plpBrazeBanner,
          topCategories,
          standardDispatchTime,
          resizeEngravingDelayTime,
          transitAndStoreProcessingTime,
          ...restOfSitePref
        } = preferenceFields('sitePreferences') || {} // sitePreferences
        const { pcpLink, brazeBanner: pdpBrazeBanner, ...restOfPDPPref } = preferenceFields('pdpPreferences') || {}
        const {
          productPromotion: cartProductPromotion,
          salesTaxTooltip,
          informationAlertBar,
          orderInformationAccordion,
          brazeContent,
          brazeBanner: cartBrazeBanner,
          ...restOfCartPref
        } = preferenceFields('cartPreferences') || {} //cartPreferences

        // @TODO: Transform fields as the components are developed
        function parseNumeric(value: any, fallback: number) {
          if (value === null || value === undefined || value === '') return fallback
          const parsed = parseInt(value, 10)
          return Number.isNaN(parsed) ? fallback : parsed
        }

        if (isPreviewEnabled) {
          // SAFETY: `data` is the flattened getEntries items; preview tooling reads `items` only, never the paging fields.
          previewStore.entries = { items: data } as EntryCollection<EntrySkeletonType, undefined, string>
        }

        return {
          site: {
            ...restOfSitePref,
            articleSignUp: articleSignUp?.fields,
            brillianceBanner: brillianceBanner?.fields,
            brillianceConfirmationPanel: brillianceConfirmationPanel?.fields,
            productPromotion: productPromotion?.[0]?.fields,
            quickSignUp: quickSignUp?.fields,
            signupCouponCode,
            signUpPanel: signUpPanel?.fields,
            memberBenefitsPanel: memberBenefitsPanel?.fields,
            loggedOutWishlistContent: loggedOutWishlistContent?.fields,
            loggedOutWishlistBanner: loggedOutWishlistBanner
              ? alertTransformer([loggedOutWishlistBanner])?.[0]
              : undefined,
            loggedInWishlistContent: loggedInWishlistContent?.fields,
            loggedInWishlistBanner: loggedInWishlistBanner
              ? alertTransformer([loggedInWishlistBanner])?.[0]
              : undefined,
            wishlistEmptyPanel,
            wishlistBanner: alertTransformer(wishlistBanner),
            searchBrazeBanner: searchBrazeBanner?.fields,
            wishlistBrazeBanner: wishlistBrazeBanner?.fields,
            plpBrazeBanner: plpBrazeBanner?.fields,
            topCategories: topCategoriesTransformer(topCategories),
            standardDispatchTime: parseNumeric(standardDispatchTime, 1),
            resizeEngravingDelayTime: parseNumeric(resizeEngravingDelayTime, 3),
            transitAndStoreProcessingTime: parseNumeric(transitAndStoreProcessingTime, 2),
          },
          pdp: {
            ...restOfPDPPref,
            pcpLink: { fields: pcpLink?.fields },
            brazeBanner: pdpBrazeBanner?.fields,
          },
          cart: {
            ...restOfCartPref,
            salesTaxTooltip: salesTaxTooltip?.fields,
            productPromotion: cartProductPromotion?.fields,
            informationAlertBar: alertTransformer(informationAlertBar),
            orderInformationAccordionPanel: orderInformationAccordion?.fields,
            brazeContent: brazeContent?.fields,
            brazeBanner: cartBrazeBanner?.fields,
          },
        }
      }

      if (data?.value) {
        site.value = markRaw(data.value.site)
        pdp.value = markRaw(data.value.pdp)
        cart.value = markRaw(data.value.cart)
      }
    } catch (err) {
      console.error(err)
    }
  }

  return { site, pdp, cart, init }
})
